/**
 * Version Distribution Chart (Bar)
 *
 * Displays tenant distribution across platform versions as a horizontal bar chart.
 * Phase 3.5-B: Multi-Tenant Visibility
 */

import React from 'react';
import type { VersionDistData } from '../../../features/dashboard/types/tenantHealth';

export interface VersionChartProps {
  data: VersionDistData | null;
  isLoading: boolean;
}

export const VersionChart: React.FC<VersionChartProps> = ({ data, isLoading }) => {
  if (isLoading && !data) {
    return (
      <div className="card card-elevated" style={{ padding: 'var(--space-4)', minHeight: 200 }}>
        <h4 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-md, 16px)', color: 'var(--text-primary)' }}>
          Version Distribution
        </h4>
        <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 'var(--space-6)' }}>
          Loading...
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="card card-elevated" style={{ padding: 'var(--space-4)', minHeight: 200 }}>
        <h4 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-md, 16px)', color: 'var(--text-primary)' }}>
          Version Distribution
        </h4>
        <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 'var(--space-6)' }}>
          No data
        </div>
      </div>
    );
  }

  const maxCount = Math.max(...data.versions.map((v) => v.count), 1);

  return (
    <div className="card card-elevated" style={{ padding: 'var(--space-4)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-3)' }}>
        <h4 style={{ margin: 0, fontSize: 'var(--text-md, 16px)', color: 'var(--text-primary)' }}>
          Version Distribution
        </h4>
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
          Latest: {data.latest_version}
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        {data.versions.map((v) => {
          const isLatest = v.version === data.latest_version;
          const barColor = isLatest
            ? 'var(--success, #22c55e)'
            : 'var(--accent-primary, #7c3aed)';

          return (
            <div key={v.version} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
              <span
                style={{
                  minWidth: 60,
                  fontSize: 'var(--text-sm)',
                  color: isLatest ? 'var(--success, #22c55e)' : 'var(--text-secondary)',
                  fontWeight: isLatest ? 600 : 400,
                  textAlign: 'right',
                }}
              >
                v{v.version}
              </span>

              <div
                style={{
                  flex: 1,
                  height: 20,
                  background: 'var(--bg-secondary, #1a1a2e)',
                  borderRadius: 'var(--radius-sm, 4px)',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${(v.count / maxCount) * 100}%`,
                    height: '100%',
                    background: barColor,
                    borderRadius: 'var(--radius-sm, 4px)',
                    transition: 'width 0.3s ease',
                    minWidth: v.count > 0 ? 4 : 0,
                  }}
                />
              </div>

              <span style={{ minWidth: 40, fontSize: 'var(--text-sm)', color: 'var(--text-primary)', fontWeight: 600 }}>
                {v.count}
              </span>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
        Total: {data.total} tenants
      </div>
    </div>
  );
};

export default VersionChart;
